import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  doc,
  getDoc,
  onSnapshot,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import { db } from '../../config/firebase';
import { RequestSeatModal } from '../../components/flexy-ride/RequestSeatModal';
import {
  MapPin,
  Calendar,
  Clock,
  Users,
  MessageCircle,
  ArrowLeft,
} from 'lucide-react';

const getSmokingLabel = (value, t) => {
  if (value === 'yes') return t('flexyRideSeat.yes');
  if (value === 'no') return t('flexyRideSeat.no');
  return t('flexyRideSeat.flexible');
};

const getPetsLabel = (value, t) => {
  if (value === 'yes') return t('flexyRideSeat.yes');
  if (value === 'no') return t('flexyRideSeat.no');
  return t('flexyRideSeat.flexible');
};

const getLuggageLabel = (value, t) => {
  if (value === 'small') return t('flexyRideSeat.luggageSmall');
  if (value === 'medium') return t('flexyRideSeat.luggageMedium');
  if (value === 'large') return t('flexyRideSeat.luggageLarge');
  return value || '-';
};

const getComfortLabel = (value, t) => {
  if (value === 'quiet') return t('flexyRideSeat.comfortQuiet');
  if (value === 'chat') return t('flexyRideSeat.comfortChat');
  return t('flexyRideSeat.flexible');
};

export default function FlexyRideSeatDetail() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const { user } = usePublicAuth();
  const { t } = useTranslation();
  const [listing, setListing] = useState(null);
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSeatModal, setShowSeatModal] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [appliedListingIds, setAppliedListingIds] = useState(new Set());

  const isOwnListing = user?.uid === listing?.createdBy;

  useEffect(() => {
    if (!listingId) {
      setError(t('flexyRideDetail.listingIdNotFound'));
      setLoading(false);
      return;
    }

    const unsub = onSnapshot(
      doc(db, 'ride_listings', listingId),
      snapshot => {
        if (!snapshot.exists()) {
          setError(t('flexyRideDetail.listingNotFound'));
          setLoading(false);
          return;
        }
        setListing({ id: snapshot.id, ...snapshot.data() });
        setLoading(false);
      },
      err => {
        console.error('Error loading listing:', err);
        setError(t('flexyRideDetail.failedToLoadListing'));
        setLoading(false);
      },
    );

    return () => unsub();
  }, [listingId]);

  useEffect(() => {
    if (!listing?.createdBy) return;

    const unsub = onSnapshot(
      doc(db, 'users', listing.createdBy),
      snapshot => {
        if (snapshot.exists()) {
          setOwnerProfile({ id: snapshot.id, ...snapshot.data() });
        }
      },
      err => {
        console.error('Error loading owner profile:', err);
      },
    );

    return () => unsub();
  }, [listing?.createdBy]);

  useEffect(() => {
    if (!user?.uid || !listingId) {
      setAppliedListingIds(new Set());
      return;
    }
    const unsub = onSnapshot(
      query(
        collection(db, 'ride_requests'),
        where('applicantId', '==', user.uid),
        where('status', 'in', [
          'pending',
          'accepted',
          'confirm_pending',
          'completed',
        ]),
      ),
      snapshot => {
        if (!snapshot?.docs) {
          setAppliedListingIds(new Set());
          return;
        }
        const ids = new Set(
          snapshot.docs.map(doc => doc.data()?.listingId).filter(Boolean),
        );
        setAppliedListingIds(ids);
      },
    );
    return () => unsub();
  }, [user?.uid, listingId]);

  const handleApply = async (listingData, { note }) => {
    if (!user?.uid) {
      alert(t('flexyRideDetail.seat.applyLoginSeat'));
      return;
    }

    if (user.uid === listingData.createdBy) {
      alert(t('flexyRideDetail.applyOwnListing'));
      return;
    }

    setSubmitLoading(true);
    try {
      const existingSnapshot = await getDocs(
        query(
          collection(db, 'ride_requests'),
          where('listingId', '==', listingData.id),
          where('applicantId', '==', user.uid),
        ),
      );

      if (!existingSnapshot.empty) {
        alert(t('flexyRideDetail.alreadyApplied'));
        setAppliedListingIds(prev => {
          const next = new Set(prev);
          next.add(listingData.id);
          return next;
        });
        setShowSeatModal(false);
        setSubmitLoading(false);
        return;
      }

      const listingOwnerName =
        ownerProfile?.displayName ||
        ownerProfile?.username ||
        ownerProfile?.email?.split('@')[0] ||
        user.displayName ||
        t('common.unknownUser');

      await addDoc(collection(db, 'ride_requests'), {
        listingId: listingData.id,
        listingOwnerId: listingData.createdBy,
        listingOwnerName: listingOwnerName,
        applicantId: user.uid,
        applicantName:
          user.displayName || user.email?.split('@')[0] || t('common.unknownUser'),
        applicantEmail: user.email,
        requesterId: user.uid,
        requesterName:
          user.displayName || user.email?.split('@')[0] || t('common.unknownUser'),
        requesterEmail: user.email,
        tripOwnerId: listingData.createdBy,
        tripOwnerName: listingOwnerName,
        status: 'pending',
        listingType: listingData.type,
        listingMode: listingData.mode || 'ride',
        note: note || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      alert(t('flexyRideDetail.requestSent'));
      setAppliedListingIds(prev => {
        const next = new Set(prev);
        next.add(listingData.id);
        return next;
      });
    } catch (error) {
      console.error('Error applying to listing:', error);
      alert(t('flexyRideDetail.failedToSendRequest'));
    } finally {
      setSubmitLoading(false);
      setShowSeatModal(false);
    }
  };

  const handleMessage = () => {
    if (!user) {
      alert(t('flexyRideDetail.seat.messageLogin'));
      return;
    }
    if (ownerProfile?.email) {
      navigate(`/chat`, { state: { participantEmail: ownerProfile.email } });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-4xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-lg">
            <div className="h-6 w-1/3 animate-pulse rounded bg-gray-200 dark:bg-slate-800" />
            <div className="mt-6 space-y-3">
              <div className="h-4 w-full animate-pulse rounded bg-gray-200 dark:bg-slate-800" />
              <div className="h-4 w-11/12 animate-pulse rounded bg-gray-200 dark:bg-slate-800" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-4xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 text-center shadow-lg">
            <p className="text-gray-600 dark:text-slate-400">
              {error || t('flexyRideDetail.listingNotFound')}
            </p>
            <Link
              to="/flexy-ride/driver"
              className="mt-4 inline-flex items-center gap-2 rounded-2xl bg-black px-5 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 dark:bg-yellow-500 dark:text-black dark:hover:bg-yellow-400"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('flexyRideDetail.backToListings')}
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const ownerName =
    ownerProfile?.displayName ||
    ownerProfile?.username ||
    ownerProfile?.email?.split('@')[0] ||
    t('tripCard.flexyUser');
  const ownerRating = ownerProfile?.averageRating ?? ownerProfile?.rating ?? 0;
  const ownerReviewCount =
    ownerProfile?.totalReviews ?? ownerProfile?.reviewsCount ?? 0;
  const isVerified = ownerProfile?.driverVerificationStatus === 'verified';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />
      <main className="mx-auto max-w-4xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/flexy-ride/driver')}
          className="mb-6 flex items-center gap-2 text-sm font-semibold text-gray-600 transition hover:text-gray-900 dark:text-slate-400 dark:hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('flexyRideDetail.backToListings')}
        </button>

        {/* Owner Profile Card */}
        {ownerProfile && (
          <div className="mb-6 rounded-3xl bg-white p-6 text-center shadow-lg dark:bg-slate-900">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-black text-3xl font-bold text-yellow-400">
              {ownerName.charAt(0).toUpperCase()}
            </div>
            <h2 className="text-xl font-extrabold text-gray-900 dark:text-white">
              {ownerName}
            </h2>
            {ownerProfile.username && (
              <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
                @{ownerProfile.username}
              </p>
            )}
            <div className="mt-3 flex items-center justify-center gap-2">
              <span className="text-lg">
                {'⭐'.repeat(Math.max(1, Math.round(ownerRating || 0)))}
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {ownerRating ? ownerRating.toFixed(1) : '—'}
              </span>
              {ownerReviewCount > 0 && (
                <span className="text-xs text-gray-500 dark:text-slate-400">
                  ({ownerReviewCount} reviews)
                </span>
              )}
            </div>
            <div className="mt-3">
              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                  isVerified
                    ? 'bg-emerald-500/10 text-emerald-400'
                    : 'bg-amber-500/10 text-amber-300'
                }`}
              >
                {isVerified
                  ? t('flexyRideDetail.verifiedDriver')
                  : t('flexyRideDetail.unverifiedDriver')}
              </span>
            </div>
          </div>
        )}

        {/* Listing Details Card */}
        <div className="rounded-3xl bg-white p-6 shadow-lg dark:bg-slate-900">
          <div className="mb-6 flex items-center justify-between">
            <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-400">
              {t('flexyRideDetail.seatBadge')}
            </span>
            {listing.price && (
              <div className="flex items-center gap-2 rounded-full bg-black px-4 py-2">
                <span className="text-sm font-bold text-yellow-400">
                  {listing.currency || 'CAD'} ${listing.price}
                </span>
                {listing.seats && (
                  <span className="text-xs text-white">
                    • {listing.seats} seats
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Route */}
          <section className="mb-6">
            <h3 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">
              {t('flexyRideDetail.route')}
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 text-gray-500 dark:text-slate-400" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                    {t('home.from')}
                  </p>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">
                    {listing.from || '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 text-gray-500 dark:text-slate-400" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                    {t('home.to')}
                  </p>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">
                    {listing.to || '-'}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <div className="my-6 border-t border-gray-200 dark:border-slate-700" />

          {/* Departure */}
          <section className="mb-6">
            <h3 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">
              {t('travelerTripRequests.departure')}
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Calendar className="mt-0.5 h-5 w-5 text-gray-500 dark:text-slate-400" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                    {t('requestDelivery.date')}
                  </p>
                  <p className="text-base text-gray-900 dark:text-white">
                    {listing.date || t('flexyRideSeat.dateNotSet')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="mt-0.5 h-5 w-5 text-gray-500 dark:text-slate-400" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                    {t('requestDelivery.time')}
                  </p>
                  <p className="text-base text-gray-900 dark:text-white">
                    {listing.time || t('flexyRideSeat.timeNotSet')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Users className="mt-0.5 h-5 w-5 text-gray-500 dark:text-slate-400" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                    {t('flexyRideSeat.seats')}
                  </p>
                  <p className="text-base text-gray-900 dark:text-white">
                    {t('flexyRideSeat.summarySeatsPriceValue', {
                      seats: listing.seats || 1,
                      price: listing.price || '0',
                    })}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <div className="my-6 border-t border-gray-200 dark:border-slate-700" />

          {/* Preferences */}
          <section className="mb-6">
            <h3 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">
              {t('flexyRideSeat.summaryPreferences')}
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  {t('flexyRideSeat.smoking')}
                </p>
                <p className="text-base text-gray-900 dark:text-white">
                  {getSmokingLabel(listing.smoking)}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  {t('flexyRideSeat.pets')}
                </p>
                <p className="text-base text-gray-900 dark:text-white">
                  {getPetsLabel(listing.pets)}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  {t('flexyRideSeat.luggage')}
                </p>
                <p className="text-base text-gray-900 dark:text-white">
                  {getLuggageLabel(listing.luggage)}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  {t('flexyRideSeat.comfort')}
                </p>
                <p className="text-base text-gray-900 dark:text-white">
                  {getComfortLabel(listing.comfort)}
                </p>
              </div>
            </div>
          </section>

          {/* Car Info */}
          {(listing.carBrand || listing.carModel || listing.carYear) && (
            <>
              <div className="my-6 border-t border-gray-200 dark:border-slate-700" />
              <section className="mb-6">
                <h3 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">
                  {t('flexyRideSeat.carInfoTitle')}
                </h3>
                <div className="space-y-2">
                  {listing.carBrand && (
                    <p className="text-base text-gray-900 dark:text-white">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                        {t('flexyRideSeat.carBrandLabel')}{' '}
                      </span>
                      {listing.carBrand}
                    </p>
                  )}
                  {listing.carModel && (
                    <p className="text-base text-gray-900 dark:text-white">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                        {t('flexyRideSeat.carModelLabel')}{' '}
                      </span>
                      {listing.carModel}
                    </p>
                  )}
                  {listing.carYear && (
                    <p className="text-base text-gray-900 dark:text-white">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                        {t('flexyRideSeat.carYearLabel')}{' '}
                      </span>
                      {listing.carYear}
                    </p>
                  )}
                </div>
              </section>
            </>
          )}

          {/* Note */}
          {listing.note && (
            <>
              <div className="my-6 border-t border-gray-200 dark:border-slate-700" />
              <section className="mb-6">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  {t('flexyRideSeat.summaryNote')}
                </p>
                <p className="text-base italic text-gray-700 dark:text-slate-300">
                  {listing.note}
                </p>
              </section>
            </>
          )}

          {/* Action Buttons */}
          {!isOwnListing && (
            <>
              <div className="my-6 border-t border-gray-200 dark:border-slate-700" />
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (!user) {
                      alert(t('flexyRideDetail.seat.applyLoginSeat'));
                      return;
                    }
                    if (appliedListingIds.has(listing.id)) {
                      alert(t('flexyRideDetail.alreadyApplied'));
                      return;
                    }
                    setShowSeatModal(true);
                  }}
                  disabled={!user || appliedListingIds.has(listing.id)}
                  className={`flex-1 rounded-2xl px-4 py-3 text-sm font-semibold transition ${
                    !user || appliedListingIds.has(listing.id)
                      ? 'cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-slate-800 dark:text-slate-500'
                      : 'bg-yellow-400 text-black hover:bg-yellow-300'
                  }`}
                >
                  {!user
                    ? t('flexyRideDetail.signInToApplySeat')
                    : appliedListingIds.has(listing.id)
                    ? t('flexyRideDetail.applied')
                    : t('flexyRideDetail.applyForSeat')}
                </button>
                <button
                  onClick={handleMessage}
                  disabled={!user}
                  className={`flex-1 rounded-2xl px-4 py-3 text-sm font-semibold transition ${
                    !user
                      ? 'cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-slate-800 dark:text-slate-500'
                      : 'flex items-center justify-center gap-2 bg-black text-yellow-400 hover:bg-gray-900 dark:bg-yellow-500 dark:text-black dark:hover:bg-yellow-400'
                  }`}
                >
                  <MessageCircle className="h-4 w-4" />
                  {t('myRequests.message')}
                </button>
              </div>
            </>
          )}
        </div>
      </main>

      <RequestSeatModal
        visible={showSeatModal}
        onClose={() => setShowSeatModal(false)}
        onSubmit={data => listing && handleApply(listing, data)}
        loading={submitLoading}
        listing={listing}
      />
    </div>
  );
}

